import React, { useEffect, useState } from "react";
import api from "../../services/axios";
import {
  ThemeProvider,
  createTheme,
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon, Edit as EditIcon } from "@mui/icons-material";

const theme = createTheme({
  palette: {
    primary: { main: "#0B74DA" },
    background: { default: "#F5F7FA", paper: "#ffffff" },
  },
  typography: { h4: { fontWeight: 700 } },
});

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [meta, setMeta] = useState({ total: 0, per_page: 10, page: 0 });
  const [search, setSearch] = useState("");

  // Dialogo para cambiar rol
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");

  const fetchUsuarios = async (page = 0, perPage = 10, searchText = "") => {
    try {
      const { data } = await api.get("/users", {
        params: { page: page + 1, per_page: perPage },
      });

      let list = data.data;
      if (searchText) {
        const lowered = searchText.toLowerCase();
        list = list.filter(
          (u) =>
            (u.name || "").toLowerCase().includes(lowered) ||
            (u.email || "").toLowerCase().includes(lowered)
        );
      }

      setUsuarios(list);
      setMeta({
        total: data.total,
        per_page: data.per_page,
        page: data.current_page - 1,
      });
    } catch (error) {
      console.error("Error obteniendo usuarios:", error);
    }
  };

  useEffect(() => { fetchUsuarios(); }, []);

  const handleChangePage = (_, newPage) => fetchUsuarios(newPage, meta.per_page, search);
  const handleChangeRowsPerPage = (e) => fetchUsuarios(0, parseInt(e.target.value, 10), search);
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    fetchUsuarios(0, meta.per_page, value);
  };

  const handleOpenDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role === "superadmin" ? "superadmin" : "user");
    setOpenDialog(true);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    try {
      const token = sessionStorage.getItem("token");
      await api.put(
        `/admin/users/${selectedUser.id}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsuarios(meta.page, meta.per_page, search);
      setOpenDialog(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error actualizando rol:", error);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, bgcolor: "background.default", minHeight: "100vh" }}>
        <Card sx={{ borderRadius: 3, position: "relative" }}>
          <CardHeader title={<Typography variant="h4">Usuarios</Typography>} sx={{ pb: 0 }} />
          <CardContent>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  value={search}
                  onChange={handleSearch}
                  fullWidth
                  placeholder="Buscar por nombre o email"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <Tooltip title="Limpiar">
                          <IconButton size="small" onClick={() => { setSearch(""); fetchUsuarios(0, meta.per_page, ""); }}>
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                  <Button variant="outlined" onClick={() => { setSearch(""); fetchUsuarios(0, meta.per_page, ""); }}>Limpiar</Button>
                </Stack>
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ boxShadow: "none", borderRadius: 2, maxHeight: "60vh" }}>
              <Table stickyHeader size="small">
                <TableHead sx={{ bgcolor: "#f0f2f5", "& .MuiTableCell-head": { fontWeight: "bold", color: "#333", fontSize: "0.9rem" } }}>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Avatar</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Registrado</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Acciones</TableCell> {/* Nueva columna */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuarios.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No hay usuarios para mostrar.</TableCell>
                    </TableRow>
                  ) : (
                    usuarios.map((u, idx) => (
                      <TableRow key={u.id || idx} hover sx={{ backgroundColor: idx % 2 === 0 ? "transparent" : "rgba(15,30,60,0.02)" }}>
                        <TableCell>{u.id}</TableCell>
                        <TableCell>
                          {u.avatar ? <Avatar src={u.avatar} alt={u.name} sx={{ width: 48, height: 48 }} /> : <Avatar sx={{ width: 48, height: 48 }}>{u.name?.[0]?.toUpperCase()}</Avatar>}
                        </TableCell>
                        <TableCell>{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.created_at ? new Date(u.created_at).toLocaleString() : "-"}</TableCell>
                        <TableCell>{u.role}</TableCell>
                        <TableCell>
                          {sessionStorage.getItem("role") === "superadmin" && u.role !== "superadmin" && (
                            <Tooltip title="Cambiar rol">
                              <IconButton size="small" color="primary" onClick={() => handleOpenDialog(u)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={meta.total}
                page={meta.page}
                onPageChange={handleChangePage}
                rowsPerPage={meta.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Filas por página"
              />
            </TableContainer>
          </CardContent>
        </Card>

        {/* Dialog para cambiar rol */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Cambiar rol de usuario</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="select-role-label">Nuevo rol</InputLabel>
              <Select
                labelId="select-role-label"
                value={newRole}
                label="Nuevo rol"
                onChange={(e) => setNewRole(e.target.value)}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleUpdateRole}>Confirmar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
